import {styled} from '@mui/material/styles'
import {Box} from '@mui/system'
import {Divider, Grid, Button, Typography, Badge, Avatar, Alert} from '@mui/material'
import {FILTERS_DRAWER_WIDTH} from '../FilterPanel/FilterPanelStyle'
import {Circle} from '@mui/icons-material'
import Switch from '@mui/material/Switch'
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip'
import {SUMMARY_DRAWER_WIDTH} from '../PersonaSidebar/PersonaSummary/SummaryComponentStyles'
import {CLOSED_WIDTH} from '../SideBar/SideBarStyle'

const PREFIX = 'DashboardComponent'

const classes = {
  dataViewer: `${PREFIX}-dataViewer`,
  dimensionSelectFormControl: `${PREFIX}-dimensionSelectFormControl`,
  dimensionSelect: `${PREFIX}-dimensionSelect`,
  selectMultiSelected: `${PREFIX}-selectMultiSelected`,
  dashboardGrid: `${PREFIX}-dashboardGrid`,
  chartContainerBody: `${PREFIX}-chartContainerBody`
}

const MainBox = styled(
  Box,
  {shouldForwardProp: (prop) => prop !== 'open'},
  {shouldForwardProp: (prop) => prop !== 'persona'}
)(({theme, open, persona}) => ({
  display: 'flex',
  flexFlow: 'column',
  maxWidth: open ? 'calc(100vw - 64px - 320px)' : 'calc(100vw - 64px)',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),

  marginLeft: `-${FILTERS_DRAWER_WIDTH}px`,
  ...(persona && !open && {marginLeft: `-${SUMMARY_DRAWER_WIDTH}px`}),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    ...(persona ? {marginLeft: `${FILTERS_DRAWER_WIDTH - SUMMARY_DRAWER_WIDTH}px`} : {marginLeft: 0})
  }),

  // persona sidebar component here...
  marginRight: 0,
  ...(persona && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: `${SUMMARY_DRAWER_WIDTH + 5}px`
  }),

  position: 'relative',
  variants: [
    {
      props: ({open}) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        })
      }
    }
  ],

  backgroundColor: theme.palette.hpBaseLighter,
  padding: theme.spacing(2),
  marginTop: theme.spacing(4),

  [`& .${classes.dimensionSelect}`]: {
    color: theme.palette.hpBlack,
    maxWidth: 350,
    paddingLeft: '10px',
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightRegular
  },

  [`& .${classes.selectMultiSelected}`]: {
    backgroundColor: 'rgba(0,0,0,0.14) !important'
  },
  [`& .${classes.chartContainerBody}`]: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightRegular
  }
}))
export const StyledLoadingContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '40vh'
}))
const StyledStatsBox = styled('div', {shouldForwardProp: (prop) => prop !== 'bannerOpen'})(({theme, bannerOpen}) => ({
  marginTop: bannerOpen ? theme.spacing(-2) : '2px',
  flexWrap: 'nowrap',
  display: 'flex',
  gap: theme.spacing(0.125)
}))

const StyledDivider = styled(Divider)(({theme}) => ({
  height: '50px',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[200]}`
}))

const StyledGrid = styled(Grid)(({theme}) => ({
  display: 'flex',
  flexShrink: 1,
  width: 'auto',
  gap: theme.spacing(0.125)
}))

const StyledDiv = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showTableView'
})(({theme}) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 0),
  flex: 1
}))

export const UniqueHouseholdsCircle = styled(Circle, {
  shouldForwardProp: (prop) => prop !== 'dotColor'
})(({theme, dotColor}) => ({
  width: theme.spacing(1.5),
  height: theme.spacing(1.5),
  marginRight: theme.spacing(1),
  color: dotColor,
  cursor: 'default'
}))
export const UniqueHouseholdsCircleGreen = styled(Circle, {
  shouldForwardProp: (prop) => prop !== 'selectedFocusLayer'
})(({theme}) => ({
  width: theme.spacing(1.5),
  height: theme.spacing(1.5),
  marginRight: theme.spacing(1),
  color: theme.palette.heatmapColor.greenDark
}))
export const UniqueHouseholdsCircleViolet = styled(Circle, {
  shouldForwardProp: (prop) => prop !== 'selectedFocusLayer'
})(({theme}) => ({
  width: theme.spacing(1.5),
  height: theme.spacing(1.5),
  marginRight: theme.spacing(1),
  color: theme.palette.hpAccentVioletPink
}))
export const UniqueHouseholdsCircleMulti = styled(Circle, {
  shouldForwardProp: (prop) => prop !== 'selectedFocusLayer'
})(({theme}) => ({
  borderRight: `6px solid ${theme.palette.hpAccentVioletPink}`,
  borderTop: `6px solid ${theme.palette.hpAccentVioletPink}`,
  borderLeft: `6px solid ${theme.palette.hpAccentGreen}`,
  borderBottom: `6px solid ${theme.palette.hpAccentGreen}`,
  transform: 'rotate(45deg)',
  borderRadius: '50%',
  display: 'inline-block',
  height: '6px',
  width: '6px',
  marginRight: theme.spacing(1)
}))

export const UniqueHouseholdsCircleOutreachSimulation = styled(Circle, {
  shouldForwardProp: (prop) => prop !== 'type'
})(({theme, type}) => ({
  width: '12px',
  height: '12px',
  marginRight: theme.spacing(1),
  cursor: 'default',
  color: type === 'enrolled' ? theme.palette.heatmapColor.greenDark : theme.palette.hpAccentVioletPink
}))

export const CircleAndTextContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center'
}))

export const StyledSpaceBox = styled(Box)(() => ({
  marginLeft: '10px'
}))

export const StyledMapFooterBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignContent: 'center',
  flexWrap: 'wrap',
  rowGap: '4px'
}))

const StyledTypography = styled(Typography)(({theme}) => ({
  color: theme.palette.hpBlack,
  textAlign: 'left',
  letterSpacing: '0.1px'
}))

const ButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bannerPresent'
})(({theme, bannerPresent}) => ({
  marginTop: bannerPresent ? theme.spacing(-0.75) : theme.spacing(1),
  marginBottom: bannerPresent ? theme.spacing(2) : theme.spacing(0),
  display: 'flex',
  alignItems: 'center'
}))

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'fontSize'
})(({theme, fontSize}) => ({
  textTransform: 'capitalize',
  letterSpacing: '0.5px',
  fontSize: fontSize || theme.typography.subtitle1.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  border: `1px solid ${theme.palette.hpPrimary}`,
  color: theme.palette.hpPrimary,
  lineHeight: 'normal',
  backgroundColor: 'transparent',
  margin: '0px 10px 0px 10px',
  height: '35px',
  width: 'auto',
  padding: '8px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    textDecoration: 'none',
    color: theme.palette.hpWhite
  },
  '.MuiButton-startIcon': {
    marginRight: '0px',
    fontSize: theme.typography.subtitle1.fontSize
  }
}))

const ExploreLabel = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  lineHeight: theme.typography.h6.lineHeight,
  display: 'inline'
}))

const StyledSwitch = styled(Switch)(({theme, isPersonaAllowed}) => ({
  padding: 8,

  '& .MuiSwitch-track': {
    borderRadius: 11,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '55%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
      fontSize: '8px',
      color: theme.palette.hpWhite,
      textAlign: 'center',
      fontFamily: theme.typography.fontFamily
    },
    '&::before': {
      content: isPersonaAllowed ? '"on"' : '""',
      fill: theme.palette.hpWhite,
      left: 12
    },
    '&::after': {
      content: isPersonaAllowed ? '"off"' : '""',
      fill: theme.palette.hpWhite, // Default color when off
      right: 12
    }
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
    color: theme.palette.hpWhite
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.hpGreenVividDark,
    opacity: 1
  }
}))

const CustomAvatar = styled(Avatar)(() => ({
  width: 50,
  height: 34
}))

const PersonasTooltip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      borderRadius: '4px',
      fontSize: theme.typography.subtitle1.fontSize,
      backgroundColor: theme.palette.hpBaseDarker,
      fontFamily: theme.typography.fontFamily,
      maxWidth: '400px',
      lineHeight: theme.typography.h2.lineHeight
    }
  })
)

const StyledBadge = styled(Badge)(({theme}) => ({
  '& .MuiBadge-badge': {
    right: '8px',
    top: '4px',
    fontSize: theme.typography.subtitle2.fontSize
  }
}))

const StyledPersonaToggleText = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightSemibold,
  fontFamily: theme.typography.fontFamily,
  lineHeight: theme.typography.h1.lineHeight,
  // Prevent accidental text selection
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none'
}))
const StickyToggleButton = styled('button')(({theme, variant}) => ({
  position: 'sticky',
  top: variant === 'show' ? '16px' : '0',
  bottom: variant === 'show' ? '32px' : undefined,
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3,
  background: theme.palette.primary.contrastText,
  color: theme.palette.primary.main,
  border: 'none',
  borderRadius: '8px 8px 0 0',
  padding: '6px 16px',
  fontWeight: theme.typography.labelLarge.fontWeight,
  fontSize: theme.typography.subtitle1.fontSize,
  cursor: 'pointer',
  '.equalizer-icon': {
    height: 18,
    width: 18,
    marginRight: 8,
    verticalAlign: 'middle'
  },
  '.expand-icon': {
    marginLeft: 8
  }
}))

const StyledAlert = styled(Alert)(({theme}) => ({
  padding: '2px',
  fontSize: theme.typography.subtitle2.fontSize,
  marginTop: '2px',
  color: theme.palette.hpAccentBlack,
  '& .MuiAlert-action': {
    alignItems: 'center'
  },
  '& svg': {
    marginTop: '2px'
  }
}))

const StyledUserFeedbackBanner = styled('div', {
  shouldForwardProp: (prop) => prop !== 'filtersOpen'
})(({theme, filtersOpen}) => ({
  position: 'fixed',
  bottom: 0,
  left: `${CLOSED_WIDTH + (filtersOpen ? FILTERS_DRAWER_WIDTH : 0)}px`,
  right: 0,
  backgroundColor: theme.palette.warning.light,
  borderTop: '1px solid #ddd',
  padding: '12px 16px',
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'space-between',
  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
}))
const StyledRightButtonsContainer = styled('div')(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-end'
}))

const PersonasPlaceholder = styled('div')(() => ({
  width: 180,
  minWidth: 180,
  display: 'inline-block'
}))

export {
  MainBox,
  classes,
  StyledStatsBox,
  StyledDivider,
  StyledGrid,
  StyledDiv,
  StyledTypography,
  ButtonContainer,
  StyledButton,
  ExploreLabel,
  StyledSwitch,
  CustomAvatar,
  PersonasTooltip,
  StyledBadge,
  StyledPersonaToggleText,
  StickyToggleButton,
  StyledAlert,
  StyledUserFeedbackBanner,
  StyledRightButtonsContainer,
  PersonasPlaceholder
}
